import React, { useState } from 'react';
import {
  Box, InputLabel, MenuItem, FormControl, Select,
  Button,
  Dialog,
  Typography,
  Alert,
} from '@mui/material';
import PropTypes from 'prop-types';
import DromoUpload from '../components/DromoUpload';

import logger from '../utils/logger';
import ImportHistory from '../components/client/ImportHistory';
import { smsLongcodeFields } from '../utils/dromoConstants';
import APIWrapper from '../utils/graphqlwrapper';

function BulkUpload(props) {
  const { client } = props;
  // for dromo upload
  const [uploadType, setUploadType] = useState('sms_longcode_groupings');
  const [importedNewData, setImportedNewData] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const [isImporterOpen, setIsImporterOpen] = useState(false);
  const [open, setOpen] = React.useState(false);

  const handleUploadTypeChange = (event) => {
    setUploadType(event.target.value);
  };

  const handleOpenDromo = () => {
    // user confirmed bulk upload for client is correct
    // open dromo uploader
    setIsImporterOpen(true);
    setOpen(false);
  };

  const handleClick = () => {
    // popup to confirm client selected is correct before opening dromo uploader
    setConfirmationPopup(true);
    setOpen(true);
  };

  /**
   * Triggers the bulk import Step Function via AppSync mutation after Dromo upload completes.
   * Sets uploadStatus to 'processing' on success or 'error' if the mutation fails.
   * @param {Object} metadata - Dromo upload metadata containing id, filename, and user
   */
  const handleUpload = async (metadata) => {
    const csvName = metadata.filename || `${metadata.id}.json`;
    try {
      await APIWrapper.queryApi(
        { query: 'bulkImport' },
        false,
        {
          input: {
            dromoId: metadata.id,
            clientId: metadata.user && metadata.user.id,
            filename: csvName,
          },
        },
      );
      setUploadStatus('processing');
    } catch (err) {
      logger.error(err);
      setUploadStatus('error');
    }
    setImportedNewData(true);
  };

  const handleRefreshHandled = () => {
    // used to reset state after user uploads a file
    // and Import History table gets refreshed
    setImportedNewData(false);
  };

  return (
    <div>
      <Box
        sx={{ mb: 1 }}
      >
        <div className="import-options">
          <FormControl
            sx={{ mx: 1 }}
            style={{ minWidth: 200 }}
          >
            <InputLabel id="bulk-upload-platform-select">Upload Type</InputLabel>
            <Select
              labelId="bulk-upload-platform-select"
              id="bulk-upload-platform-select"
              value={uploadType}
              label="Upload Type"
              onChange={handleUploadTypeChange}
            >
              <MenuItem value="sms_longcode_groupings">SMS Longcode Groupings</MenuItem>
            </Select>
          </FormControl>
        </div>
        <Box
          sx={{ padding: 1 }}
        >
          <Button variant="contained" onClick={handleClick}>Upload Codes</Button>
          {uploadStatus === 'processing' && (
            <Alert severity="info" sx={{ mt: 1 }}>Import submitted — processing. Refresh to check results.</Alert>
          )}
          {uploadStatus === 'error' && (
            <Alert severity="error" sx={{ mt: 1 }}>Import failed to process. Please try again.</Alert>
          )}
        </Box>
        {
          confirmationPopup && (
            <Dialog open={open} maxWidth="sm" fullWidth>
              <Box sx={{
                display: 'flex', flexDirection: 'column', gap: 3, padding: 2, justifyContent: 'center', margin: 'auto',
              }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: 'rgb(72, 101, 129)',
                    fontWeight: 600,
                    fontSize: '1.2rem',
                    lineHeight: 1.4,
                  }}
                >
                  Are you sure you want to start a bulk upload for
                  {' '}
                  {client}
                  ?
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button variant="outlined" onClick={() => setOpen(false)} sx={{ width: '45%', fontWeight: 600 }}>No</Button>
                  <Button variant="contained" onClick={handleOpenDromo} sx={{ width: '45%', fontWeight: 600 }}>Yes</Button>
                </Box>
              </Box>
            </Dialog>
          )
        }
        {isImporterOpen && (
          <DromoUpload
            isImporterOpen={isImporterOpen}
            setIsImporterOpen={setIsImporterOpen}
            importFields={smsLongcodeFields}
            importIdentifier="SMS Longcode Groupings"
            allowCustomFields={false}
            userObject={{
              id: client,
            }}
            bulkRowHooks={[]}
            handleUpload={handleUpload}
          />
        )}

      </Box>
      <Box
        sx={{ mt: 3 }}
      >
        <ImportHistory
          client={client}
          importedNewData={importedNewData}
          handleRefreshHandled={handleRefreshHandled}
          type="p2p_bulk_upload"
        />
      </Box>
    </div>
  );
}

export default BulkUpload;

BulkUpload.propTypes = {
  client: PropTypes.string,
};
